<template>
    <div
        class="SplitButton"
        :class="className"
    >
        <div class="SplitButton__button-container">
            <!--
                Fired when an option is clicked
                @event select
                @property value The value of the selected option
            -->
            <button
                :disabled="disabled"
                :class="[
                    'SplitButton__button',
                    !showDropdownButton && 'SplitButton__button--single-option',
                    `SplitButton__button--${size}`
                ]"
                @click.stop="mainButtonClick"
            >
                {{ options[0].label }}
            </button>
            <button
                v-if="showDropdownButton"
                :disabled="disabled"
                class="SplitButton__select-button"
                :class="[
                    `SplitButton__select-button--${size}`
                ]"
                @click.stop="toggleClicked"
            >
                <StreamlineIcon
                    class="SplitButton__arrow-icon"
                    fill="#FFFFFF"
                    :icon="ArrowIcon"
                    :size="12"
                />
            </button>
        </div>
        <div
            v-if="opened"
            class="SplitButton__options-container"
        >
            <DropdownOption
                v-for="opt in parsedOption"
                :key="opt.label"
                class="SplitButton__option"
                :value="opt.value"
                :label="opt.label"
                :icon="opt.icon"
                :icon-position="optionIconPosition"
                :color="opt.color"
                :description="opt.description"
                no-border
                @select="select(opt.value)"
            />
        </div>
    </div>
</template>

<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {ArrowUp1, ArrowDown1} from "@reviewpush/streamline-bold/lib/arrows-diagrams/Arrows";
import cloneDeep from 'lodash/cloneDeep';
import stat from 'status';
import statusBuilder from 'status/statusBuilder';
import DropdownOption from "../FormElements/RPDropdown/_dropdown-option.vue";

export default {
    name: 'RPSplitButton',

    components: {
        StreamlineIcon,
        DropdownOption
    },

    props: {
        /**
         * An array of objects, each with a `label` and `value` property
         */
        options: {
            type: Array,
            required: true,
            validator: (a) => a.length > 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'lg'
        },
        /**
         * A status pulled from the common `status` constants (`/src/status/index.js`)
         */
        status: {
            type: String,
            default: stat.STANDBY,
        },
        /**
         * When true, the first option will simply be a label, and the visible button
         * will toggle the dropdown.
         */
        mimicDropdown: {
            type: Boolean,
            default: false,
        },
        optionIconPosition: {
            type: String,
            default: 'right',
        }
    },

    data() {
        return {
            opened: false,
        };
    },
    computed: {
        ArrowIcon(){
            return this.opened
                ? ArrowUp1
                : ArrowDown1;
        },
        showDropdownButton(){
            return this.options.length > 1;
        },
        parsedOption(){
            return cloneDeep(this.options).splice(1);
        },
        className() {
            return statusBuilder(
                'SplitButton',
                this.status,
                this.disabled,
                {
                    [this.size]: true,
                }
            );
        },
    },
    mounted() {
        document.addEventListener('click', this.close);
    },
    destroyed() {
        document.removeEventListener('click', this.close);
    },
    methods: {
        close(e) {
            if(!this.$el.contains(e.target)) {
                this.opened = false;
            }
        },
        toggleClicked() {
            this.opened = !this.opened;
        },
        select(val){
            this.opened = false;
            this.$emit('select', val);
        },
        mainButtonClick() {
            this.opened = false;
            if (this.mimicDropdown) {
                this.toggleClicked();
            } else {
                this.$emit('select', this.options[0].value);
            }
        }
    },
};
</script>

<style lang="scss">
   .SplitButton  {
    width: fit-content;
    font-family: proxima-nova, sans-serif;
    border-radius: 4px;
    background: $white;
    height: 2rem;
    position: relative;

    &--xl {
        height: 3rem;
    }
    &--lg {
        height: 2.5rem;
    }
    &--md {
        height: 2rem;
    }
    &--sm {
        height: 1.5rem;
    }

    &__button-container {
        display:flex;
        height: inherit;
    }

    &__button {
        font-family: proxima-nova, sans-serif;
        padding: 0 1rem;
        background: $primary;
        border-color: $primary;
        border-radius: 4px 0 0 4px;
        margin-right: 1px;
        color: $white;
        font-weight: 600;
        cursor:pointer;
        white-space: nowrap;

        &:hover { background: $primary-400; }
        &:active { background: $primary-500; }
        &:disabled {
            cursor: default;
            border-color: $primary-100;
            background: $primary-100;
        }
        &:focus {
            outline:none;
            box-shadow: 0 0 4px $primary;
            border-radius: 4px 0 0 4px;
        }
        &--single-option {
            border-radius: 4px;
            &:focus {
                outline:none;
                box-shadow: 0 0 4px $primary;
                border-radius: 4px;
            }
        }

        &--xl {
            height: 3rem;
        }
        &--lg {
            height: 2.5rem;
        }
        &--md {
            height: 2rem;
        }
        &--sm {
            height: 1.5rem;
        }
    }
    &__select-button {
        width: 2.5rem;
        padding: 0;
        background: $primary;
        border-color: $primary;
        border-radius: 0 4px 4px 0;
        align-items: center;
        cursor:pointer;
        &:hover { background: $primary-400; }
        &:active { background: $primary-500; }
        &:disabled {
            cursor: default;
            border-color: $primary-100;
            background: $primary-100
        }
        &:focus {
            outline:none;
            box-shadow: 0 0 4px $primary;
            border-radius: 0 4px 4px 0;
        }
        &--xl {
            height: 3rem;
        }
        &--lg {
            height: 2.5rem;
        }
        &--md {
            height: 2rem;
        }
        &--sm {
            height: 1.5rem;
        }
    }

    &__options-container {
        min-width: 100%;
        max-width: fit-content;
        position:absolute;
        right: 0;
        z-index: 1000;
        background: $white;
        border: 1px solid $grey-100;
        box-sizing: border-box;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
        border-radius: 4px;
        padding: 0.5rem;
        margin-top: 0.25rem;
        display: flex;
        flex-direction: column;
        white-space: nowrap;
    }
    &__option {
        border-bottom: none;
    }
}
</style>

<docs>
```vue
<RPSplitButton
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
```

```vue
import { StairsAscend } from "@reviewpush/streamline-regular/lib/wayfinding/Stairs";
<p>With icons on the dropdown items (can be positioned on left or right):</p><br>

<RPSplitButton
    option-icon-position="left"
    :options="[
        {label: 'Don\'t Go Upstairs', value: 'dont-go-upstairs'},
        {label: 'Go Upstairs', value: 'go-upstairs', icon: StairsAscend},
    ]"
></RPSplitButton>
```

```vue
<p>The split button normally acts as a primary button, with secondary actions displayed by clicking the caret.</p>
<p>It can also mimic normal dropdown behavior, where clicking the default/current option opens the dropdown.</p>
<br>
<RPSplitButton
    :mimic-dropdown="true"
    :options="[
        {label: 'Create User', value: ''},
        {label: 'Create Single User', value: 'single'},
        {label: 'Bulk Import Users', value: 'bulk'}
    ]"
></RPSplitButton>
```
</docs>
